import classNames from '@/utils/classNames';
import type { ComponentPropsWithoutRef, CSSProperties, ElementType, ReactElement, Ref } from 'react';
import { forwardRef } from 'react';
import styles from './Stack.module.css';

type StackProps<T extends ElementType = 'div'> = {
    as?: T;
    $gap?: string;
    $half?: boolean;
    double?: boolean;
    $dividers?: boolean;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | '$gap' | '$half' | '$dividers'>;

/** A container that spaces out all immediate children on the standard grid */
function StackInner<T extends ElementType = 'div'>(
    { as, className, $gap, $half, double, $dividers, style, ...props }: StackProps<T>,
    ref: Ref<Element>
): ReactElement {
    const Component: ElementType = as || 'div';
    return (
        <Component
            ref={ref}
            className={classNames(
                styles.stack,
                $half && styles.half,
                double && styles.double,
                $dividers && styles.dividers,
                className
            )}
            style={{ '--stack-gap': $gap, ...style } as CSSProperties}
            {...props}
        />
    );
}

export const Stack = forwardRef(StackInner) as <T extends ElementType = 'div'>(
    props: StackProps<T> & { ref?: Ref<Element> }
) => ReactElement;
